import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import styles from "./FilterBar.module.css";

export type FilterType = "text" | "dateRange" | "select";

export interface FilterDef {
	key: string;
	type: FilterType;
	label: string;
	placeholder?: string;
	width?: number;
	options?: { value: string; label: string }[];
}

interface FilterBarProps {
	filters: FilterDef[];
	values: Record<string, string>;
	onChange: (values: Record<string, string>) => void;
}

export function FilterBar({ filters, values, onChange }: FilterBarProps) {
	const { t } = useTranslation();
	const [dateDraft, setDateDraft] = useState<Record<string, string>>({});
	const dateRangeRefs = useRef<Record<string, HTMLDivElement | null>>({});

	const displayValues = { ...values, ...dateDraft };

	const update = (key: string, value: string) => {
		const next = { ...values, [key]: value };
		if (!value) delete next[key];
		onChange(next);
	};

	const commitDateRange = (draft: Record<string, string>) => {
		if (Object.keys(draft).length === 0) return;
		const next = { ...values, ...draft };
		for (const key of Object.keys(draft)) {
			if (!next[key]) delete next[key];
		}
		setDateDraft({});
		onChange(next);
	};

	const updateDateRange = (key: string, value: string) => {
		if (key.endsWith("To")) {
			commitDateRange({ ...dateDraft, [key]: value });
		} else {
			setDateDraft((prev) => ({ ...prev, [key]: value }));
		}
	};

	const handleDateRangeBlur = (e: React.FocusEvent<HTMLDivElement>, groupKey: string) => {
		const container = dateRangeRefs.current[groupKey];
		const nextFocus = e.relatedTarget as Node | null;
		if (container && nextFocus && container.contains(nextFocus)) return;
		commitDateRange(dateDraft);
	};

	const clear = (key: string) => {
		const next = { ...values };
		delete next[key];
		setDateDraft({});
		onChange(next);
	};

	const clearAll = () => {
		setDateDraft({});
		onChange({});
	};

	const active = filters.filter((f) => {
		const v = displayValues[f.key];
		return v && v !== "";
	});

	return (
		<div className={styles.container}>
			<div className={styles.row}>
				{filters.map((f) => (
					<div key={f.key} className={styles.filter}>
						{f.type === "text" && (
							<Input
								placeholder={f.placeholder ?? f.label}
								value={values[f.key] ?? ""}
								onChange={(e) => update(f.key, e.target.value)}
								className={styles.input}
								style={f.width ? { width: f.width } : undefined}
							/>
						)}
						{f.type === "dateRange" && (
							<div
								className={styles.dateRange}
								ref={(el) => {
									dateRangeRefs.current[f.key] = el;
								}}
								onBlur={(e) => handleDateRangeBlur(e, f.key)}
							>
								<Input
									type="date"
									value={displayValues[`${f.key}From`] ?? ""}
									onChange={(e) => updateDateRange(`${f.key}From`, e.target.value)}
									className={styles.dateInput}
								/>
								<span className={styles.dateSeparator}>–</span>
								<Input
									type="date"
									value={displayValues[`${f.key}To`] ?? ""}
									onChange={(e) => updateDateRange(`${f.key}To`, e.target.value)}
									className={styles.dateInput}
								/>
							</div>
						)}
						{f.type === "select" && f.options && (
							<Select
								value={values[f.key] ?? ""}
								onChange={(e) => update(f.key, e.target.value)}
								options={[{ value: "", label: t("all") }, ...f.options]}
								className={styles.select}
							/>
						)}
					</div>
				))}
			</div>
			{active.length > 0 && (
				<div className={styles.badges}>
					{active.map((f) => (
						<span key={f.key} className={styles.badge}>
							{f.label}:{" "}
							{f.type === "select"
								? (f.options?.find((o) => o.value === displayValues[f.key])?.label ??
									displayValues[f.key])
								: displayValues[f.key]}
							<button type="button" onClick={() => clear(f.key)} className={styles.badgeClear}>
								<X size={12} />
							</button>
						</span>
					))}
					<button type="button" onClick={clearAll} className={styles.clearAll}>
						{t("clearAll")}
					</button>
				</div>
			)}
		</div>
	);
}
